import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, Divider, Space} from 'antd';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
  StyledApprovalsTab,
  StyledApprovalsTabSpace,
  StyledApprovalsTabReviewsCount,
  StyledApprovalsTabActionsSpace
} from './StyledApprovalsTab';
import UserReview from '../../../../UserReview';
import {dateHelpers, reviewHelpers} from '../../../../../utils/helpers';
import {reviewApproverTypeConstants} from '../../../../../constants';


const ApprovalsTab = ({
  entity,
  inReviewMode = false,
  onApprove,
  onDiscardAutoApproval,
  onReassign,
  onReject,
  onReviewEdit,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOverrideChecked, setIsOverrideChecked] = React.useState(false);

  const reviewButtonProps = { disabled: !isOverrideChecked };

  const {employeeEmail, isAdmin} = useSelector(state => ({
    employeeEmail: state?.user?.employee?.email,
    isAdmin: state?.user.isAdmin
  }));

  const isAlreadyAdminApproved = useMemo(() => {
    return reviewHelpers.isAlreadyApprovedByAdmin(entity);
  }, [entity?.approval]); // eslint-disable-line react-hooks/exhaustive-deps

  const {anyAdminReviews, autoReviews, reviews, isAvailableAnyAdminReassign, isAvailableAutoReassign} = useMemo(() => {
    const anyAdminReviews = reviewHelpers.getAnyAdminApprovals(entity);
    const autoReviews = reviewHelpers.getAutoApprovals(entity);
    const reviews = reviewHelpers.getApprovals(entity);
    const isAvailableAnyAdminReassign = anyAdminReviews.length > 0 && reviews.length === 0;
    const isAvailableAutoReassign = autoReviews.length > 0 && reviews.length === 0;
    return {
      anyAdminReviews,
      autoReviews,
      isAvailableAnyAdminReassign,
      isAvailableAutoReassign,
      reviews
    }
  }, [entity?.approval]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdminApprovalAvailable = useMemo(() => {
    const isInApprovalList = reviewHelpers.isEmployeeInApprovalList(entity, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(entity, employeeEmail);
    const isAnyAdminApproval = reviewHelpers.isAnyAdminApproval(entity);

    if (isAdmin && isAnyAdminApproval) return true;
    return isAdmin && inReviewMode && !isInApprovalList && !isAlreadyApproved;
  }, [isAdmin, employeeEmail, entity?.approval, inReviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOverrideChecked) setIsOverrideChecked(false);
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = (e, override = false) => onApprove?.({e, entity, data: { admin_override: override }});

  const handleReject = (e, override = false) => onReject?.({e, entity, data: { admin_override: override }});

  const handleReassign = (e, value, id) => onReassign?.(e, { current_approver: employeeEmail, new_approver: value, approval_id: id });

  const handleReassignAnyAdmin = (e, value) => {
    onReassign?.(e, { current_approver: reviewApproverTypeConstants.ANY_ADMIN, new_approver: value });
  }

  const handleEditAutoApproval = (e) => {
    onDiscardAutoApproval?.(e, { current_approver: reviewApproverTypeConstants.AUTO_APPROVAL, new_approver: employeeEmail });
  }

  const renderReview = (review, idx) => {
    const id = review?.id;
    const isApproved = review?.is_approved;
    const date = dateHelpers.getDateFromISO(review.approval_date, 'DD MMM YYYY, HH:mm');
    const canEdit = review?.approver?.email === employeeEmail;
    const status = isApproved === true
      ? 'approved' : isApproved === false ? 'rejected' : canEdit ? 'pending' : 'default';

    return (
      <React.Fragment key={review.id}>
        <UserReview
          date={date}
          canEdit={canEdit}
          canReassign={canEdit}
          message={review.comment}
          reviewer={review.approver}
          status={status}
          onApprove={(e) => onApprove?.({e, entity, id})}
          onEdit={(e) => onReviewEdit?.({e, entity, id})}
          onReject={(e) => onReject?.({e, entity, id})}
          onReassign={(e, value) => handleReassign(e, value, id)}
        />
        {idx < reviews.length - 1 && <Divider style={{ margin: '10px 0', width: 32 }} />}
      </React.Fragment>
    );
  }

  return (
    <StyledApprovalsTab {...rest}>
      {isAlreadyAdminApproved && (
        <Space className='w-100' direction='vertical' size='middle'>
          <Checkbox checked disabled>{t('adminOverride')}</Checkbox>
          {reviews.filter(r => r.admin_override).map(renderReview)}
        </Space>
      )}
      {!isAlreadyAdminApproved && (
        <StyledApprovalsTabSpace direction='vertical' size={10}>
          {reviews.length > 1 && (
            <StyledApprovalsTabReviewsCount>
              {t('parallelReviews')} ({reviews.length})
            </StyledApprovalsTabReviewsCount>
          )}
          <StyledApprovalsTabSpace direction='vertical' size={0}>
            {reviews.map(renderReview)}

            {isAvailableAnyAdminReassign && (
              <UserReview
                className='mb-20'
                canReassign={isAdmin}
                reviewer={anyAdminReviews?.[0].approver}
                status='pending'
                onReassign={handleReassignAnyAdmin}
              />
            )}

            {isAvailableAutoReassign && (
              <UserReview
                className='mb-20'
                canEdit={isAdmin}
                reassignButtonText={t('edit')}
                reviewer={autoReviews?.[0].approver}
                status='auto-approval'
                onEdit={handleEditAutoApproval}
              />
            )}
          </StyledApprovalsTabSpace>
          {isAdminApprovalAvailable && (
            <React.Fragment>
              {reviews.length > 0 && <Divider style={{ margin: '14px 0' }} />}
              <StyledApprovalsTabActionsSpace size='middle' direction='vertical'>
                <Checkbox checked={isOverrideChecked} onChange={() => setIsOverrideChecked(!isOverrideChecked)}>
                  {t('adminOverride')}
                </Checkbox>
                <UserReview.ReviewButtons
                  approveButtonProps={{ ...reviewButtonProps, onClick: (e) => handleApprove(e, true) }}
                  className='approvals-tab-review-buttons'
                  rejectButtonProps={{ ...reviewButtonProps, onClick: (e) => handleReject(e, true) }}
                />
              </StyledApprovalsTabActionsSpace>
            </React.Fragment>
          )}
        </StyledApprovalsTabSpace>
      )}
    </StyledApprovalsTab>
  );
}

ApprovalsTab.propTypes = {
  entity: PropTypes.object,
  inReviewMode: PropTypes.bool,
  onApprove: PropTypes.func,
  onDiscardAutoApproval: PropTypes.func,
  onReassign: PropTypes.func,
  onReject: PropTypes.func,
  onReviewEdit: PropTypes.func,
}

export default ApprovalsTab;
